import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FaIcons from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';

const ThankYou = () => {
  return (
    <>
      <Helmet>
        <title>Thank You | AGIR USA</title>
        <meta name="description" content="Your donation is changing lives. Thank you for supporting AGIR USA." />
      </Helmet>

      <div className="bg-surface min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          
          {/* Main Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-5xl mx-auto border border-gray-100"
          >
            <div className="grid md:grid-cols-2">
              {/* Left: Emotional Image */}
              <div className="relative h-64 md:h-auto min-h-[400px]">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e85f8df4ca1088998577b5cca07c0363.jpeg" 
                  alt="A child smiling with hope" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-8">
                  <div className="text-white">
                    <p className="font-heading italic text-xl mb-1">"I used to dream of school. Now, I live it."</p>
                    <p className="text-sm font-bold uppercase tracking-widest opacity-80">— Mariam, Age 9</p>
                  </div>
                </div>
              </div>

              {/* Right: Gratitude Content */}
              <div className="p-8 md:p-12 md:py-16 flex flex-col justify-center relative">
                {/* Success Indicator */}
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: "spring" }}
                  className="absolute top-8 right-8 text-green-500 bg-green-50 rounded-full p-2"
                >
                  <SafeIcon icon={FaIcons.FaCheckCircle} className="text-3xl" />
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h1 className="font-heading text-4xl lg:text-5xl font-bold text-primary-dark mb-6 leading-tight">
                    Because of You,<br /><span className="text-secondary">Hope Grows.</span>
                  </h1>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                    Thank you from the bottom of our hearts. Your generous gift has been securely received.
                  </p>
                  <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                    While you are reading this, your support is already going to work—filling a plate, opening a book, and reminding a child in Guinea-Bissau that they are loved and valued.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button to="/" variant="outline" className="w-full sm:w-auto justify-center">
                      Return Home
                    </Button>
                    <div className="text-center sm:text-left flex items-center justify-center sm:justify-start gap-2 text-sm text-gray-400 font-medium px-4">
                      <SafeIcon icon={FiIcons.FiMail} /> Check your email for a receipt.
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Impact Reinforcement Strip */}
            <div className="bg-primary-dark text-white p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="font-heading text-2xl font-bold text-secondary mb-2">Your Impact is Real</h3>
                <p className="text-white/70">Here is exactly what your support makes possible today.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-primary-dark text-xl shrink-0">
                    <SafeIcon icon={FaIcons.FaBowlFood} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Daily Nutrition</h4>
                    <p className="text-sm text-white/70">Fighting hunger daily</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-primary-dark text-xl shrink-0">
                    <SafeIcon icon={FaIcons.FaBookOpen} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Quality Education</h4>
                    <p className="text-sm text-white/70">Breaking poverty cycles</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center text-primary-dark text-xl shrink-0">
                    <SafeIcon icon={FaIcons.FaHeart} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Spiritual Care</h4>
                    <p className="text-sm text-white/70">Faith & emotional support</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Community Invitation - Modified to remove social media */}
            <div className="bg-secondary/10 p-8 md:p-12 text-center">
              <h3 className="font-heading text-2xl font-bold text-primary-dark mb-4">You Are Now Part of the Family</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We are deeply grateful for your partnership. Together, we are building a brighter future for the children of Guinea-Bissau.
              </p>
            </div>

          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ThankYou;